<template>
  <div v-show="Display" class="pub-form-container" @click.self="close">
      <form class="pub-form" @submit.prevent="handleSubmit">
        <!-- Cover image -->
        <label class="field-label" for="pub-cover">
        Cover image
        <span v-if="isCompressingImg" class="compressing-badge">Optimizing…</span>
        </label>
        <div
        id="pub-cover"
        class="cover-drop"
        :style="coverPreview ? { backgroundImage: `url(${coverPreview})` } : null"
        @click="triggerFilePicker"
        >
        <div class="cover-drop__scrim"></div>
        <div class="cover-drop__content">
            <svg viewBox="0 0 24 24" fill="none"><path d="M4 16.5V18a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1.5M12 3v13m0-13-4 4m4-4 4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <span>{{ coverPreview ? 'Change cover' : 'Upload a cover image' }}</span>
        </div>
        <button
            v-if="coverPreview"
            type="button"
            class="cover-drop__remove"
            aria-label="Remove cover image"
            @click.stop="removeCover"
        >
            <svg style="min-width:24px" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        </div>
        <input
        ref="fileInput"
        type="file"
        accept="image/*"
        class="visually-hidden"
        @change="onCoverChange"
        />
        <p v-if="errors.cover" class="field-error">{{ errors.cover }}</p>

        <!-- Description -->
        <label class="field-label" for="pub-description">Description</label>
        <textarea
        id="pub-description"
        v-model="description"
        class="textarea"
        rows="3"
        maxlength="280"
        placeholder="What's this live about?"
        ></textarea>
        <div class="field-hint">
        <span>{{ description.length }}/280</span>
        </div>
        <p v-if="errors.description" class="field-error">{{ errors.description }}</p>

        <!-- Type -->
        <span class="field-label">Room type</span>
        <div class="type-toggle" role="radiogroup" aria-label="Room type">
        <button
            type="button"
            class="type-toggle__option"
            :class="{ 'type-toggle__option--active': type === 'public' }"
            role="radio"
            :aria-checked="type === 'public'"
            @click="type = 'public'"
        >
            <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/><path d="M3 12h18M12 3c2.5 2.6 3.8 5.7 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.7-3.8-9S9.5 5.6 12 3Z" stroke="currentColor" stroke-width="2"/></svg>
            Public
        </button>
        <button
            type="button"
            class="type-toggle__option"
            :class="{ 'type-toggle__option--active': type === 'private' }"
            role="radio"
            :aria-checked="type === 'private'"
            @click="type = 'private'"
        >
            <svg viewBox="0 0 24 24" fill="none"><rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" stroke-width="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4" stroke="currentColor" stroke-width="2"/></svg>
            Private
        </button>
        </div>

        <!-- Tags -->
        <label class="field-label" for="pub-tag-input">Tags</label>
        <div class="tags-input-wrap" @click="$refs.tagInputRef.focus()">
        <div class="tags-list">
            <span v-for="tag in tags" :key="tag" class="tag-pill">
            #{{ tag }}
            <button type="button" class="tag-remove" :aria-label="`Remove tag ${tag}`" @click="removeTag(tag)">×</button>
            </span>
            <input
            id="pub-tag-input"
            ref="tagInputRef"
            v-model="tagInput"
            class="tag-input"
            placeholder="Add tag..."
            maxlength="30"
            @keydown.enter.prevent="addTag"
            @keydown.,.prevent="addTag"
            @keydown.space.prevent="addTag"
            @blur="setTag"
            />
        </div>
        </div>
        <p class="field-hint">Enter, comma or space to add · max 10</p>

        <!-- Participants -->
        <span class="field-label">Participants</span>

        <ul v-if="selectedParticipants.length" class="chip-list">
        <li v-for="user in selectedParticipants" :key="user.uid" class="chip">
            <span class="chip__avatar" :style="{ background: avatarColor(user.pseudo) }">{{ initials(user.pseudo) }}</span>
            <span class="chip__name">{{ user.pseudo }} </span>
            <button type="button" class="chip__remove" :aria-label="`Remove ${user.pseudo}`" @click="removeParticipant(user.uid)">
                <svg style="min-width: 24px;" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
        </li>
        </ul>

        <button
        type="button"
        class="add-participant-btn"
        :disabled="selectedParticipants.length >= maxParticipants"
        @click="pickerOpen = true"
        >
        <svg viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        Add participant
        <span class="add-participant-btn__count">{{ selectedParticipants.length }}/{{ maxParticipants }}</span>
        </button>
        <p v-if="errors.ongoingConference" class="field-error">{{ errors.ongoingConference }}</p>
        <!-- Submit -->
        <button type="submit" class="submit-btn" :disabled="isLoading" :class="{ 'submit-btn--loading': isLoading }">
        <span v-if="isLoading" class="spinner" aria-hidden="true"></span>
        {{ isLoading ? 'Loading....' : 'Start the conference' }}
        </button>

        <ParticipantPicker
        :open="pickerOpen"
        :model-value="selectedParticipants"
        :max="props.maxParticipants"
        @update:model-value="selectedParticipants = $event"
        @close="pickerOpen = false"
        />
    </form>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, reactive, inject } from 'vue'
import ParticipantPicker from './ParticipantPicker.vue'
// npm install browser-image-compression
import imageCompression from 'browser-image-compression'
import { useStore } from 'vuex'

const store = useStore()

const axios = inject('axios')

const props = defineProps({
  // Current user creating/hosting the publication.
  adminUserId: {
    type: String,
    required: true
  },
  // Pool of people the "Add participant" search can pick from.
  // Wire this to your real user search / contacts API.
  participantsPool: {
    type: Array,
    default: () => []
  },
  maxParticipants: {
    type: Number,
    default: 10
  }
})

const emit = defineEmits(['showConference'])

const Display = ref(false)
const fileInput = ref(null)
const coverFile = ref(null)
const coverPreview = ref('')
const description = ref('')
const type = ref('public')
const selectedParticipants = ref([])
const pickerOpen = ref(false)

const tagInputRef = ref(null)
const tagInput = ref('')
const tags = ref([])

const isCompressingImg = ref(false)
const isLoading = ref(false)

const errors = reactive({
  cover: '',
  description: '',
  participants: '',
  ongoingConference:''
})

function addTag() {
  const val = tagInput.value.trim().replace(/^#/, '').replace(/[,\s]/g, '')
  if (val && !tags.value.includes(val) && tags.value.length < 10) tags.value.push(val)
  tagInput.value = ''
}

function setTag() {
  const val = tagInput.value.split(/[,\s]+/).filter(Boolean);
  if(val) tags.value = [...tags.value, ...val].slice(0,10)
  tagInput.value = ''
}

function removeTag(tag) {
  tags.value = tags.value.filter((t) => t !== tag)
}

function triggerFilePicker() {
  fileInput.value?.click()
}

function onCoverChange(event) {
  const file = event.target.files?.[0]
  if (!file) return
  coverFile.value = file
  errors.cover = ''

  const reader = new FileReader()
  reader.onload = () => {
    coverPreview.value = reader.result
  }
  reader.readAsDataURL(file)
}

function removeCover() {
  coverFile.value = null
  coverPreview.value = ''
  if (fileInput.value) fileInput.value.value = ''
}

function removeParticipant(userUid) {
  selectedParticipants.value = selectedParticipants.value.filter((user) => user.uid !== userUid)
}

function validate() {
  errors.cover = ''
  errors.description = ''

  if (!coverFile.value) errors.cover = 'Please add a cover image.'
  if (!description.value.trim()) errors.description = 'Please add a short description.'

  return !errors.cover && !errors.description
}

async function handleSubmit() {
  if (!validate() || isLoading.value) return

  isLoading.value = true

  try {
    // Compress the cover image before sending it off.
    isCompressingImg.value = true
    let compressedCoverFile = coverFile.value
    if(!(coverFile.value.type === "image/svg+xml")) { // if it is not an svg
      const compressedCover = await imageCompression(coverFile.value, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        fileType: 'image/jpeg',
        initialQuality: 0.82
      })
      compressedCoverFile = new File(
        [compressedCover],
        coverFile.value.name.replace(/\.[^.]+$/, '') + '.jpg',
        { type: 'image/jpeg' }
      )
    }

    const actors = selectedParticipants.value.map(user => user.uid);

    const form = new FormData();
    form.append("type", "create");
    form.append("text", description.value.trim());
    form.append("type1", type.value);
    form.append("curveType", 5);
    form.append("curveColor", 3);
    form.append("image", compressedCoverFile);
    form.append("actors", JSON.stringify(actors));
    form.append("tags", JSON.stringify(tags.value));

    const res = await axios.post('/conference.php',
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
    )
    //-- fetching users data ------------------
    const usersToGet = new Set()
    actors.forEach(usr => {
      if(!store.state.users[usr.userId]) usersToGet.add(usr.userId) // if the haven't been already saved
    });
    // get user metadata
    if(usersToGet.size > 0) {
      const res2 = await axios.post('user.php',{type:"getUsersMetaData",users:[...usersToGet]})
      const users = res2.data || []
      users.forEach(us => {
        store.commit('setAnUser',us)
      })
    }
    //------------------------

    emit('showConference', res.data)
    resetForm()
    close()
  } catch (err) {
    if(err.status === 409) { 
      errors.ongoingConference = "Impossible ! You are already in a conference."
      return
    }
    if(err.response?.data?.error === "You've reached your daily live limit.") { 
      errors.ongoingConference = "You've reached your daily live limit."
      return
    }
    console.error('Cover image compression failed:', err)
    //errors.cover = 'Could not process the cover image, please try another one.'
    errors.ongoingConference = "error"
  } finally {
    isCompressingImg.value = false
    isLoading.value = false
  }
}


function resetForm() {
  coverFile.value = null
  coverPreview.value = ''
  description.value = ''
  type.value = 'public'
  selectedParticipants.value = []
  tags.value = []
  tagInput.value = ''
  if (fileInput.value) fileInput.value.value = ''
}

/* ---------- Avatar helpers (kept in sync with LiveCard.vue) ---------- */
const avatarPalette = [
  'linear-gradient(135deg,#38bdf8,#6366f1)',
  'linear-gradient(135deg,#ec4899,#8b5cf6)',
  'linear-gradient(135deg,#22d3ee,#0ea5e9)',
  'linear-gradient(135deg,#f97316,#ec4899)',
  'linear-gradient(135deg,#a78bfa,#38bdf8)'
]

function hashString(str = '') {
  let h = 0
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i)
    h |= 0
  }
  return Math.abs(h)
}

function avatarColor(pseudo) {
  return avatarPalette[hashString(pseudo) % avatarPalette.length]
}

function initials(pseudo = '') {
  return pseudo.trim().slice(0, 2).toUpperCase()
}

function close () {
  Display.value = false
}
function show () {
  Display.value = true
}
defineExpose({ show,close, Display })
</script>

<style scoped>
.pub-form-container {
    position: absolute;
    top:0px;
    left:0px;
    width: 100%;
    height:100%;
    display: flex; justify-content: center; align-items: center;
    z-index: 2000;
    background: rgba(6, 9, 18, 0.6);
}
.pub-form {
  --bg-card: #10162a;
  --bg-input: rgba(148, 163, 184, 0.06);
  --border: rgba(148, 163, 184, 0.1);
  --cyan: #22d3ee;
  --text-primary: #e7ebf5;
  --text-secondary: #8592ad;
  --text-muted: #4b5670;
  --danger: #fb3d68;

  font-family: 'Inter', 'Sora', system-ui, -apple-system, sans-serif;
  color: var(--text-primary);
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 18px;
  width: 300px;
  height: 100%;
  max-height: 90%;
  overflow: auto;
}

.pub-form > * {
  margin-top: 14px;
}

.pub-form > *:first-child {
  margin-top: 0;
}

.field-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

/* ---- Cover ---- */
.cover-drop {
  position: relative;
  aspect-ratio: 16 / 9;
  min-height: 120px;
  border-radius: 14px;
  border: 1.5px dashed var(--border);
  background-size: cover;
  background-position: center;
  background-color: rgba(148, 163, 184, 0.04);
  cursor: pointer;
  overflow: hidden;
  margin-top: 8px;
}

.cover-drop__scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(10, 14, 26, 0.1), rgba(10, 14, 26, 0.55));
}

.cover-drop__content {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
}

.cover-drop__content svg {
  width: 22px;
  height: 22px;
  margin-bottom: 6px;
  color: var(--cyan);
}

.cover-drop__remove {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: rgba(10, 14, 26, 0.65);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding:0px;
}

.cover-drop__remove svg {
  width: 12px;
  height: 12px;
}

/* ---- Textarea ---- */
.textarea {
  width: 100%;
  margin-top: 8px;
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: 12px;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 14.5px;
  line-height: 1.45;
  padding: 12px;
  resize: vertical;
  box-sizing: border-box;
}

.textarea::placeholder {
  color: var(--text-muted);
}

.field-hint {
  display: flex;
  justify-content: flex-end;
  font-size: 11.5px;
  color: var(--text-muted);
  margin-top: 4px;
}

.field-error {
  font-size: 12px;
  color: var(--danger);
  margin-top: 4px;
}

.compressing-badge {
  display: inline-block;
  margin-left: 8px;
  font-size: 11px;
  font-weight: 600;
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 6px;
  padding: 2px 7px;
}

/* ---- Tags ---- */
.tags-input-wrap {
  margin-top: 8px;
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 8px 10px;
  cursor: text;
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.tag-pill {
  display: inline-flex;
  align-items: center;
  background: rgba(34, 211, 238, 0.09);
  border: 1px solid rgba(34, 211, 238, 0.22);
  border-radius: 999px;
  color: var(--cyan);
  font-size: 12px;
  font-weight: 600;
  padding: 3px 8px;
  margin-right: 6px;
  margin-bottom: 6px;
}

.tag-remove {
  border: none;
  background: none;
  color: rgba(34, 211, 238, 0.6);
  font-size: 15px;
  line-height: 1;
  margin-left: 5px;
  padding: 0;
  cursor: pointer;
}

.tag-remove:hover {
  color: var(--danger);
}

.tag-input {
  flex: 1;
  min-width: 70px;
  border: none;
  outline: none;
  background: none;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 13.5px;
  padding: 3px 0;
  margin-bottom: 6px;
}

.tag-input::placeholder {
  color: var(--text-muted);
}

/* ---- Type toggle ---- */
.type-toggle {
  display: flex;
  margin-top: 8px;
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 4px;
}

.type-toggle__option {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--text-muted);
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 600;
  padding: 9px 10px;
  border-radius: 9px;
  cursor: pointer;
}

.type-toggle__option svg {
  width: 15px;
  height: 15px;
  margin-right: 6px;
}

.type-toggle__option--active {
  background: linear-gradient(135deg, #22d3ee, #6366f1);
  color: #fff;
}

/* ---- Participants ---- */
.chip-list {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  margin: 8px 0 0;
  padding: 0;
}

.chip {
  display: flex;
  align-items: center;
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 4px 8px 4px 4px;
  margin-right: 8px;
  margin-bottom: 8px;
}

.chip__avatar {
  width: 22px;
  height: 22px;
  margin-right: 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: #fff;
}

.chip__name {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-primary);
  max-width: 170px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chip__remove {
  width: 18px;
  height: 18px;
  margin-left: 6px;
  border: none;
  background: rgba(148, 163, 184, 0.14);
  border-radius: 50%;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.chip__remove svg {
  width: 8px;
  height: 8px;
}

.add-participant-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 8px;
  padding: 11px 14px;
  border-radius: 12px;
  border: 1.5px dashed var(--border);
  background: transparent;
  color: var(--cyan);
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
}

.add-participant-btn svg {
  width: 15px;
  height: 15px;
  margin-right: 6px;
}

.add-participant-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.add-participant-btn__count {
  margin-left: 8px;
  color: var(--text-muted);
  font-weight: 600;
}

/* ---- Submit ---- */
.submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 13px 14px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #ec4899, #8b5cf6);
  color: #fff;
  font-family: inherit;
  font-size: 14.5px;
  font-weight: 700;
  cursor: pointer;
}

.submit-btn:disabled {
  cursor: not-allowed;
}

.submit-btn--loading {
  opacity: 0.85;
}

.spinner {
  width: 15px;
  height: 15px;
  margin-right: 8px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 340px) {
  .field-label { font-size: 12px; }
  .textarea { font-size: 13.5px; }
  .type-toggle__option { font-size: 12.5px; }
  .add-participant-btn { font-size: 12.5px; }
  .submit-btn { font-size: 13.5px; }
  .compressing-badge { font-size: 10px; }
  .tag-pill { font-size: 11px; }
  .tag-input { font-size: 12.5px; }
}
</style>